// A posting whose send had an unknown outcome (audit D10): KaziPeople sent the
// journal, the ledger's answer was lost, and it may already be there. The only
// safe next step is a person looking — so this offers exactly the two answers
// they can come back with, and no "try again".
//
// Shared by every place that posts to the ledger (payroll run, payroll
// settlement, expense report, expense settlement).
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { HelpCircle, Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { resolvePosting, type AccountingProvider, type Posting } from '@/services/accountingService';

export interface UnknownPostingResolverProps {
  provider: AccountingProvider;
  legalEntityId: string;
  kind: 'payroll_accrual' | 'payroll_settlement' | 'expense_report' | 'expense_settlement';
  sourceId: string;
  systemName: string;
  canResolve: boolean;
  onResolved: (p: Posting) => void;
}

export const UnknownPostingResolver: React.FC<UnknownPostingResolverProps> = ({
  provider, legalEntityId, kind, sourceId, systemName, canResolve, onResolved,
}) => {
  const { t } = useTranslation(['payroll']);
  const confirm = useConfirm();
  const [ledgerId, setLedgerId] = useState('');
  const [busy, setBusy] = useState(false);

  const resolve = async (outcome: 'found' | 'not_found') => {
    if (outcome === 'not_found') {
      const ok = await confirm({
        title: t('accounting.unknownNotFoundTitle', { defaultValue: 'Not in {{system}}?', system: systemName }),
        message: t('accounting.unknownNotFoundBody', {
          defaultValue: 'Only say this after searching {{system}} for this journal. It will then be posted again — if it was there after all, it would be posted twice.',
          system: systemName,
        }),
        confirmLabel: t('accounting.unknownNotFoundConfirm', { defaultValue: 'It is not there' }),
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const p = await resolvePosting(provider, legalEntityId, kind, sourceId, outcome, ledgerId.trim());
      onResolved(p);
      toast.success(outcome === 'found'
        ? t('accounting.unknownFoundOk', { defaultValue: 'Recorded as posted.' })
        : t('accounting.unknownNotFoundOk', { defaultValue: 'Marked as not posted — you can post it now.' }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.unknownResolveFailed', { defaultValue: 'Could not record that.' })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-warning/40 p-3 text-sm" data-testid="unknown-posting">
      <div className="flex items-start gap-2">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <p className="text-foreground">
          {t('accounting.unknownBody', {
            defaultValue: 'We sent this to {{system}} but its answer was lost, so it may already be posted. Search {{system}} for this journal before doing anything else.',
            system: systemName,
          })}
        </p>
      </div>
      {canResolve ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            aria-label={t('accounting.unknownLedgerId', { defaultValue: 'Journal id in {{system}}', system: systemName })}
            placeholder={t('accounting.unknownLedgerIdPlaceholder', { defaultValue: 'Journal id in {{system}}', system: systemName })}
            className="h-8 w-44 rounded-md border border-border bg-background px-2 text-xs text-foreground"
            value={ledgerId}
            disabled={busy}
            onChange={(e) => setLedgerId(e.target.value)}
          />
          <Button size="sm" disabled={busy || !ledgerId.trim()} onClick={() => void resolve('found')}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {t('accounting.unknownFound', { defaultValue: 'It is there — record it' })}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void resolve('not_found')}>
            {t('accounting.unknownNotFound', { defaultValue: 'It is not there' })}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t('accounting.unknownNeedsFinance', { defaultValue: 'Finance resolves this after checking {{system}}.', system: systemName })}
        </p>
      )}
    </div>
  );
};
